import { defineComponent as _defineComponent } from 'vue'
import { computed, inject, onMounted, ref } from "vue";
import Page from "@/components/shared/Page.vue";
import NavigationBar from "@/components/shared/NavigationBar.vue";
import { useRoute } from "vue-router";
import {
  PROVIDE_KEY_REMOVE_LOADING_IMAGE,
  PROVIDE_KEY_SHOW_ERROR_MODAL,
  PROVIDE_KEY_SHOW_LOADING_IMAGE,
  PROVIDE_KEY_SHOW_TOAST_MESSAGE,
  RemoveLoadingImage,
  SERVICE_UID,
  ShowErrorModal,
  ShowLoadingImage,
  ShowToastMessage,
} from "@/types";
import { useBbsStore } from "@/stores/bbsStore";
import router from "@/router";
import { ILostFoundDetail } from "@/domain/entities/bbs/lostFound/lostFoundDetail.types";
import TextInput from "@/components/shared/inputs/TextInput.vue";
import LabeledCustomSelect from "@/components/shared/selects/LabeledCustomSelect.vue";
import {
  FilterKeyValueType,
  IFilter,
  IOption,
} from "@/domain/entities/filter/filter.types";
import dayjs from "dayjs";
import {
  GetOptions,
  HttpBaseFilter,
  OptionMapper,
} from "@/repositories/lineInfo/lineInfoRepository.types";
import { useLineStore } from "@/stores/lineStore";
import TargetCalendarInput from "@/components/shared/inputs/TargetCalendarInput.vue";
import FooterButton from "@/components/shared/button/FooterButton.vue";
import BaseTextarea from "@/components/shared/inputs/base/BaseTextarea.vue";
import ScrollTopControl from "@/components/shared/ScrollTopControl.vue";


const __default__ = {
  name: "LostEdit",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props, { expose: __expose }) {
  __expose();

const showErrorModal = inject(PROVIDE_KEY_SHOW_ERROR_MODAL) as ShowErrorModal;
const showLoadingImage = inject(
  PROVIDE_KEY_SHOW_LOADING_IMAGE
) as ShowLoadingImage;
const removeLoadingImage = inject(
  PROVIDE_KEY_REMOVE_LOADING_IMAGE
) as RemoveLoadingImage;
const showToastMessage = inject(
  PROVIDE_KEY_SHOW_TOAST_MESSAGE
) as ShowToastMessage;

const route = useRoute();
const lineStore = useLineStore();
const bbsStore = useBbsStore();

const lostUid = route.params.uid?.toString(); // 분실물 상세에서 수정 버튼을 눌러서 들어온 경우에만 가지고 있음
const lostDetail = ref<ILostFoundDetail>({} as ILostFoundDetail);
const contentWrapper = ref<HTMLElement>();

const FieldKey = {
  title: "title",
  allocUid: "allocUid",
  lineName: "lineName",
  lostTs: "lostTs",
  lostNote: "lostNote",
} as const;

const isValidRequiredFields = computed(
  () =>
    !!lostDetail.value?.[FieldKey.title] &&
    (!!lostDetail.value?.[FieldKey.allocUid] ||
      !!lostDetail.value?.[FieldKey.lineName]) &&
    !!lostDetail.value?.[FieldKey.lostTs] &&
    !!lostDetail.value?.[FieldKey.lostNote]
);

const updateLostDetail = <K extends keyof ILostFoundDetail>(
  key: K,
  value: ILostFoundDetail[K]
) => {
  lostDetail.value[key] = value;
};

const onUpdateLostTs = (date: string) => {
  updateLostDetail(FieldKey.lostTs, date);
};

// filter 정의
const boardedLineFilter = ref<IFilter>();

const boardedLineFilterMapper: OptionMapper<HttpBaseFilter> = (data) => {
  const { allocUid, lineRouteName, lineRouteNo, realOnDtm } = data;
  const lineName =
    lineRouteName === lineRouteNo
      ? lineRouteName
      : `${lineRouteNo}(${lineRouteName})`;

  const realOnDateTime = dayjs(realOnDtm).format("YYYY-MM-DD HH:mm");

  return {
    key: allocUid,
    name: `${lineName} (${realOnDateTime})`,
    displayText: lineName,
    optionMetadata: {
      [FieldKey.lostTs]: realOnDateTime,
    },
  };
};

const getFilter = async (
  pathParam: string,
  key: string,
  filterMapper: OptionMapper<HttpBaseFilter>,
  isPublic: boolean,
  multiple = false,
  label?: string,
  dummyOptionName?: string,
  defaultOptionCode?: string,
  getOptions?: GetOptions<any>,
  allowDirectInput = false
) => {
  const result = await lineStore.getFilter(
    pathParam,
    key,
    FilterKeyValueType.FilterKey_OptionKey,
    filterMapper,
    isPublic,
    multiple,
    label,
    dummyOptionName,
    defaultOptionCode,
    undefined,
    undefined,
    undefined,
    getOptions,
    allowDirectInput
  );

  if (!result.isSuccess) {
    showToastMessage(`${label} 필터 목록을 가져오지 못했습니다`);
    return;
  }

  return result.data;
};

const updateBoardedLineOptions = (filter?: IFilter) => {
  if (!filter) return;

  // 직접 입력한 노선인지 여부
  const isDirectInputLine = !!lostDetail.value?.lineName?.length;

  if (!isDirectInputLine) return;

  toggleShouldShowLineDirectInput(isDirectInputLine);

  filter.availableOptions = filter.availableOptions.map((option) => {
    if (option.isDirectInput) {
      option.default = isDirectInputLine;
    }
    return option;
  });
};

const getFilters = async () => {
  const today = dayjs().format("YYYY-MM-DD");
  const oneMonthAgo = dayjs().subtract(1, "month").format("YYYY-MM-DD");

  const [boardedLineResult] = await Promise.all([
    getFilter(
      `/service/${SERVICE_UID}/board?boardDateFrom=${oneMonthAgo}&boardDateTo=${today}`,
      FieldKey.allocUid,
      boardedLineFilterMapper,
      false,
      false,
      "분실노선",
      undefined,
      lostDetail.value?.allocUid,
      undefined,
      true
    ),
  ]);

  boardedLineFilter.value = boardedLineResult;
  updateBoardedLineOptions(boardedLineFilter.value);
};

const shouldShowLineDirectInput = ref(false);
const toggleShouldShowLineDirectInput = (show: boolean) => {
  shouldShowLineDirectInput.value = show;
};

const updateSelectedOptions = (filter: IFilter, option: IOption) => {
  filter.selectedOptions = option;
};

const handleBoardedLine = (option: IOption) => {
  // 분실노선에서 직접입력을 선택한 경우 노선 직접입력용 input을 화면에 표시한다.
  toggleShouldShowLineDirectInput(!option.key);
  if (option.key) {
    // 직접입력한 노선명 제거
    updateBoardedLineName("");
  }
  // 특정노선을 클릭한 경우, 분실일시(=탑승일시)를 자동으로 입력해준다.
  onUpdateLostTs(option.optionMetadata?.[FieldKey.lostTs]);
};

const updateBoardedLineName = (name: string) => {
  updateLostDetail(FieldKey.lineName, name);
};

const onSelectOption = (filter: IFilter, option: IOption) => {
  if (!filter.key) return;

  updateSelectedOptions(filter, option);

  if (filter.key === FieldKey.allocUid) {
    handleBoardedLine(option);
  }

  updateLostDetail(filter.key as keyof ILostFoundDetail, option.key);
};

const handleError = (title: string, result: any) => {
  showErrorModal(
    title,
    result.errorMessage,
    result.resultCode,
    result.errorTime
  );
};

const submitPost = () => {
  if (lostUid) {
    requestUpdateLostDetail(lostDetail.value);
  } else {
    createLostDetail(lostDetail.value);
  }
};

const createLostDetail = async (lostDetail: ILostFoundDetail) => {
  showLoadingImage();
  const createResult = await bbsStore.createLostFoundDetail(lostDetail);
  removeLoadingImage();

  if (!createResult.isSuccess) {
    handleError("분실물 신고등록 오류", createResult);
    return;
  }

  showToastMessage("분실물 신고가 등록되었습니다.");
  goToBack();
};

const requestUpdateLostDetail = async (lostDetail: ILostFoundDetail) => {
  showLoadingImage();
  const updateResult = await bbsStore.updateLostFoundDetail(lostDetail);
  removeLoadingImage();

  if (!updateResult.isSuccess) {
    handleError("분실물 신고수정 오류", updateResult);
    return;
  }

  showToastMessage("분실물 신고가 수정되었습니다.");
  goToBack();
};

const initLostDetailIfNeeded = async () => {
  if (!lostUid) return;

  const lostFoundDetail = bbsStore.lostFoundDetail;
  if (lostFoundDetail?.uid === lostUid) {
    lostDetail.value = lostFoundDetail;
  } else {
    lostDetail.value =
      (await getLostDetail(lostUid)) ?? ({} as ILostFoundDetail);
  }
};

const getLostDetail = async (uid: string) => {
  showLoadingImage();
  const result = await bbsStore.getLostFoundDetail(uid);
  removeLoadingImage();

  if (!result.isSuccess) {
    handleError("분실물 상세 조회 오류", result);
    return;
  }

  return result.data;
};

const goToBack = () => {
  router.currentRoute.value.meta.willGoBack = true;
  router.back();
};

onMounted(async () => {
  await initLostDetailIfNeeded();
  await getFilters();
});

const __returned__ = { showErrorModal, showLoadingImage, removeLoadingImage, showToastMessage, route, lineStore, bbsStore, lostUid, lostDetail, contentWrapper, FieldKey, isValidRequiredFields, updateLostDetail, onUpdateLostTs, boardedLineFilter, boardedLineFilterMapper, getFilter, updateBoardedLineOptions, getFilters, shouldShowLineDirectInput, toggleShouldShowLineDirectInput, updateSelectedOptions, handleBoardedLine, updateBoardedLineName, onSelectOption, handleError, submitPost, createLostDetail, requestUpdateLostDetail, initLostDetailIfNeeded, getLostDetail, goToBack, Page, NavigationBar, TextInput, LabeledCustomSelect, TargetCalendarInput, FooterButton, BaseTextarea, ScrollTopControl }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})